// react
import React, { useEffect, useMemo, useState } from 'react';

// packages
import { Controller, useWatch } from 'react-hook-form';

// types
import type {
  Option,
  RegionAutocompleteProps,
  SubdivisionWithChildren,
} from 'components/RegionAutocomplete/types';

// components
import Autocomplete from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';

const StyledListbox = styled('ul')({
  paddingTop: '0 !important',
  paddingBottom: 8,
  margin: 0,
  listStyle: 'none',
  maxHeight: '40vh',
  overflow: 'auto',
  '& li.MuiAutocomplete-group ul': {
    paddingTop: 0,
    marginTop: 0,
  },
});

const RegionAutocomplete = function ({
  control,
  initialRegion,
  name,
  country,
  statesFromCountry,
  getCountrySubdivisions,
  getChildSubdivisionMatchingCountryAndState,
  label,
  disabled = false,
  setValue,
}: RegionAutocompleteProps) {
  // state
  const [currentStates, setCurrentStates] = useState<
    SubdivisionWithChildren[] | null
  >(statesFromCountry);
  const [menuOpen, setMenuOpen] = useState(false);

  // cast name to the required template literal type for useWatch
  const typedName = name as
    | `${string}`
    | `${string}.${string}`
    | `${string}.${number}`;

  const watchedValue = useWatch({ control, name: typedName });

  // clear region and update states when country changes
  useEffect(() => {
    if (!country) {
      setCurrentStates(null);
      setValue(name, '');
      return;
    }

    setValue(name, '');

    const states = getCountrySubdivisions(country);

    setCurrentStates(states);
  }, [country, getCountrySubdivisions, name, setValue]);

  // prefill with 'n/a' if that's the only option
  useEffect(() => {
    if (currentStates?.length === 1 && currentStates[0].code === 'n/a')
      setValue(typedName, 'n/a');
  }, [currentStates, typedName, setValue]);

  // keep synced with prop changes
  useEffect(() => {
    setCurrentStates(statesFromCountry);
  }, [statesFromCountry]);

  useEffect(() => {
    if (!initialRegion) return;

    setValue(name, initialRegion);
  }, [initialRegion, name, setValue]);

  // handle changes when menu opens
  useEffect(() => {
    if (!menuOpen) return;

    const timer = setTimeout(() => {
      const states = getCountrySubdivisions(country);
      setCurrentStates(states);
    }, 0);

    return () => clearTimeout(timer);
  }, [menuOpen, country, getCountrySubdivisions]);

  // handle changes when menu closes
  useEffect(() => {
    if (menuOpen || !country) return;

    const timer = setTimeout(() => {
      const states = getChildSubdivisionMatchingCountryAndState(
        country,
        watchedValue
      );

      setCurrentStates(states);
    }, 0);

    return () => clearTimeout(timer);
  }, [
    menuOpen,
    country,
    watchedValue,
    getChildSubdivisionMatchingCountryAndState,
  ]);

  const flatOptions: Option[] = useMemo(() => {
    if (!currentStates) return [];

    if (currentStates.length === 1 && currentStates[0].code === 'n/a') {
      return [{ label: 'n/a', value: 'n/a' }];
    }

    const options: Option[] = [];

    currentStates.forEach((state) => {
      if (state.children) {
        state.children.forEach((child) => {
          options.push({
            label: child.name,
            value: child.code.split('-')[1],
            group: state.name,
          });
        });
      } else {
        options.push({
          label: state.name,
          value: state.code.split('-')[1],
        });
      }
    });

    return options;
  }, [currentStates]);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: 'Region is required.' }}
      render={({
        field: { value, onChange, ref, ...rest },
        fieldState: { error },
      }) => (
        <Autocomplete
          options={flatOptions}
          groupBy={(option) => option.group || ''}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.value === val?.value}
          value={flatOptions.find((opt) => opt.value === value) || null}
          onChange={(_, newValue) => onChange(newValue?.value || '')}
          disabled={disabled || !currentStates || currentStates.length === 0}
          open={menuOpen}
          onOpen={() => setMenuOpen(true)}
          onClose={() => setMenuOpen(false)}
          ListboxComponent={StyledListbox}
          renderGroup={(params) => (
            <React.Fragment key={params.key}>
              <ListSubheader
                sx={{
                  padding: '12px',
                  lineHeight: 1.5,
                  textAlign: 'center',
                }}
              >
                {params.group}
              </ListSubheader>
              {params.children}
            </React.Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoComplete="new-password"
              variant="filled"
              label={label}
              error={!!error}
              helperText={error?.message ?? ' '}
              inputRef={ref}
              sx={{
                '& .MuiFilledInput-input': {
                  paddingTop: '4px !important',
                  paddingBottom: '5px !important',
                },
              }}
            />
          )}
          {...rest}
        />
      )}
    />
  );
};

export default RegionAutocomplete;
